// shared/actor-row-loader.ts — Janus lane N6 (2026-10-01): the ONE way a ported GM / sheet action gets the actor's row.
//
// The truth lanes (resource / status / charge / sanguine / equip / crafting) decide an action against the actor's compiled row and lazily
// save it keyed on that row's own revision (`encounter_creatures.movement_rev`). Until now each lane looked only at the rows the page
// already held (`FateTide.encounterCache`) and, when it held none, handed the click to the legacy PHP endpoint, which compiled the row
// server-side and judged the rule there. That hand-back is what keeps the legacy twins alive (board R-043…R-046, R-064, R-066,
// R-071…R-074, R-083, R-088, R-089, R-101). This module replaces it:
//
//   ensureActorRowHeld(actorId, encounterId?, opts?) → Promise<ActorRowResolution>
//     1. HELD     — the row is in an encounter blob the page holds (the hint, the active encounter, then every cached encounter).
//     2. LOADED   — the page does not hold it, but the actor's encounter is known (the hint, else the caster row's own
//                   `encounter_creatures.encounter_id`, the caster row fetched once when the page lacks it too): the encounter blob is
//                   fetched into the page cache through the existing viewer-projected read (`ftResyncEncounter` →
//                   get_encounter_arr.php) and the row is re-resolved. Both answer `{ kind: 'encounter', encounterId, actorId, row }` —
//                   the lane posts through its normal per-actor outbox.
//     3. ROW-ONLY — the actor belongs to NO encounter (a PC sheet used outside play: `encounter_creatures.encounter_id` NULL / ''):
//                   `{ kind: 'row', encounterId: '', actorId, row }`, the creature / PC row itself (the caster cache). The lane decides
//                   against it and saves through `postRowOnly()` (the commit authorizes from that row and CASes its revision). Only when
//                   the caller passes `allowRowOnly` (its commit accepts encounter-less rows); otherwise a refusal.
//     4. REFUSED  — nothing usable: `{ kind: 'refused', reason, message }`. The caller shows the words; it NEVER posts a legacy endpoint.
//                   A dangling encounter reference, an unreadable encounter or a blob without the actor are refusals, never row-only (a
//                   row that lives in an encounter must change through that encounter's frames, or its viewers go stale).
//
//   findHeldActorRow(actorId, hint?) — step 1 only, synchronous (for a caller that must stay synchronous).
//   runRowOnly(actorId, task)         — one row-only action at a time per actor, across every lane (resolve → post → apply).
//   postRowOnly(url, body)            — the ROW-ONLY transport: the SAME body (same operation id) retried on a lost answer / 5xx, never
//                                       on a 4xx; answers { status, body }.
//
// Pure data + injected effects (`ActorRowLoaderDeps`), so tools/test/actor-row-loader.test.mjs drives it without a page.
type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** A Map-like cache (the page caches are Maps; keys may be stored in either case). */
interface CacheLike { get(key: string): unknown; keys(): Iterable<string> }

export interface HeldActorRow { kind: 'encounter'; encounterId: string; actorId: string; row: Row; loaded: boolean }
export interface RowOnlyActor { kind: 'row'; encounterId: ''; actorId: string; row: Row }
export type ActorRowRefusalReason = 'bad-id' | 'no-row' | 'encounter-unreadable' | 'not-in-encounter' | 'row-only-unsupported';
export interface ActorRowRefusal { kind: 'refused'; reason: ActorRowRefusalReason; message: string }
export type ActorRowResolution = HeldActorRow | RowOnlyActor | ActorRowRefusal;

export interface ActorRowLoaderDeps {
  encounterCache(): CacheLike | null | undefined;
  casterCache(): CacheLike | null | undefined;
  activeEncounterId(): string;
  /** Fetch the encounter blob into encounterCache (the page's own heal read). Rejects / resolves; the loader re-resolves either way. */
  resyncEncounter?: (encounterId: string) => Promise<unknown>;
  /** Fetch the caster row into casterCache. */
  fetchCaster?: (actorId: string) => Promise<unknown>;
  fetchImpl?: (url: string, init: RequestInit) => Promise<Response>;
  baseUrl?: () => string;
  sleep?: (ms: number) => Promise<void>;
  /** Reads that refresh between attempts own their retry budget. Saves retain three identical attempts. */
  maxAttempts?: 1 | 2 | 3;
}

function cacheGet(cache: CacheLike | null | undefined, id: string): unknown {
  if (!cache) return undefined;
  const direct = cache.get(id) ?? cache.get(id.toLowerCase());
  if (direct !== undefined) return direct;
  for (const key of cache.keys()) if (upper(key) === id) return cache.get(key);
  return undefined;
}
function ecIdOf(row: unknown): string { return upper(asRow(asRow(row)?.encounter_creatures)?.encounter_creature_id); }
function rowInEncounter(cache: CacheLike | null | undefined, encounterId: string, actorId: string): Row | null {
  const roster = asRow(cacheGet(cache, encounterId))?.encounter_creatures_arr;
  if (!Array.isArray(roster)) return null;
  return (roster.find(r => ecIdOf(r) === actorId) as Row | undefined) ?? null;
}
/** The actor's caster-cache row, when the page holds it (a PC sheet, a creature card). */
function casterRow(deps: ActorRowLoaderDeps, actorId: string): Row | null {
  const row = asRow(cacheGet(deps.casterCache(), actorId));
  return row && (ecIdOf(row) === actorId || !ecIdOf(row)) ? row : null;
}

export function defaultActorRowLoaderDeps(): ActorRowLoaderDeps {
  const w = (typeof window !== 'undefined' ? window : {}) as unknown as {
    FateTide?: { encounterCache?: CacheLike; casterCache?: CacheLike };
    ftGetActiveEncounterId?: () => unknown;
    ftResyncEncounter?: (id: string) => Promise<unknown>;
    fetchAndCacheCaster?: (id: string, opts?: { skipEncounterSeed?: boolean; throwOnError?: boolean }) => Promise<unknown>;
    BASE_URL?: string;
  };
  return {
    encounterCache: () => w.FateTide?.encounterCache,
    casterCache: () => w.FateTide?.casterCache,
    activeEncounterId: () => upper(w.ftGetActiveEncounterId?.()),
    resyncEncounter: typeof w.ftResyncEncounter === 'function' ? (id: string) => w.ftResyncEncounter!(id) : undefined,
    fetchCaster: typeof w.fetchAndCacheCaster === 'function' ? (id: string) => w.fetchAndCacheCaster!(id, { skipEncounterSeed: true }) : undefined,
    baseUrl: () => String(w.BASE_URL ?? '/'),
  };
}

/** Step 1: the row the page already holds, or null. Synchronous. */
export function findHeldActorRow(actorIdRaw: unknown, hint?: unknown, deps: ActorRowLoaderDeps = defaultActorRowLoaderDeps()): HeldActorRow | null {
  const actorId = upper(actorIdRaw);
  if (!UUID.test(actorId)) return null;
  const cache = deps.encounterCache();
  const ids = [upper(hint), deps.activeEncounterId()].filter(Boolean);
  for (const key of cache?.keys() ?? []) ids.push(upper(key));
  for (const encounterId of new Set(ids)) {
    const row = rowInEncounter(cache, encounterId, actorId);
    if (row) return { kind: 'encounter', encounterId, actorId, row, loaded: false };
  }
  return null;
}

const MESSAGES: Record<ActorRowRefusalReason, string> = {
  'bad-id': 'Nothing was changed: no creature was named.',
  'no-row': 'Nothing was changed: this creature could not be loaded on this page. Reload the page and try again.',
  'encounter-unreadable': 'Nothing was changed: this creature\'s encounter could not be loaded on this page. Reload the page and try again.',
  'not-in-encounter': 'Nothing was changed: this creature is no longer in its encounter. Reload the page and try again.',
  'row-only-unsupported': 'Nothing was changed: this action needs the creature in an encounter. Open it from its campaign and try again.',
};
export function actorRowRefusal(reason: ActorRowRefusalReason, what?: string): ActorRowRefusal {
  const message = what ? MESSAGES[reason].replace('Nothing was changed', `${what} was not changed`) : MESSAGES[reason];
  return { kind: 'refused', reason, message };
}

/** Steps 1–4. Never throws; never posts a legacy endpoint. */
export async function ensureActorRowHeld(actorIdRaw: unknown, encounterHint?: unknown, opts: { allowRowOnly?: boolean; what?: string } = {},
  deps: ActorRowLoaderDeps = defaultActorRowLoaderDeps()): Promise<ActorRowResolution> {
  const actorId = upper(actorIdRaw);
  if (!UUID.test(actorId)) return actorRowRefusal('bad-id', opts.what);
  const held = findHeldActorRow(actorId, encounterHint, deps);
  if (held) return held;
  // The actor's own row (caster cache), fetched once when the page lacks it: it names the actor's encounter.
  let own = casterRow(deps, actorId);
  if (!own && deps.fetchCaster) {
    try { await deps.fetchCaster(actorId); } catch { /* a refusal below */ }
    own = casterRow(deps, actorId);
  }
  const stored = upper(asRow(own?.encounter_creatures)?.encounter_id);
  const hint = upper(encounterHint);
  // A non-empty stored reference that is not an id is unreadable, never 'no encounter' (Codex N6 review P2).
  if (stored && !UUID.test(stored)) return actorRowRefusal('encounter-unreadable', opts.what);
  const encounterId = UUID.test(stored) ? stored : (!own && UUID.test(hint) ? hint : '');
  if (encounterId) {
    if (!deps.resyncEncounter) return actorRowRefusal('encounter-unreadable', opts.what);
    let failed = false;
    try { await deps.resyncEncounter(encounterId); } catch { failed = true; }
    const row = rowInEncounter(deps.encounterCache(), encounterId, actorId);
    if (row) return { kind: 'encounter', encounterId, actorId, row, loaded: true };
    // A viewer-projected blob may omit the actor; an unreadable / dangling encounter never becomes a row-only save.
    return actorRowRefusal(failed || !asRow(cacheGet(deps.encounterCache(), encounterId)) ? 'encounter-unreadable' : 'not-in-encounter', opts.what);
  }
  if (!own) return actorRowRefusal('no-row', opts.what);
  if (!opts.allowRowOnly) return actorRowRefusal('row-only-unsupported', opts.what);
  return { kind: 'row', encounterId: '', actorId, row: own };
}

// ── the ROW-ONLY transport ───────────────────────────────────────────────────────────────────────────────────────────────────────────
const rowOnlyChains = new Map<string, Promise<unknown>>();
export interface RowOnlyAnswer { status: number; body: Row | null }
/**
 * Run a row-only action for `actorId` — resolve `before` from the row, post, apply the answer — one at a time per actor across every
 * lane, so a second click resolves against the row the first one's answer left (never two writes resolved from the same `before`).
 */
export function runRowOnly<T>(actorIdRaw: unknown, task: () => Promise<T>): Promise<T> {
  const key = upper(actorIdRaw);
  const prior = rowOnlyChains.get(key) ?? Promise.resolve();
  const next = prior.then(task, task);
  rowOnlyChains.set(key, next);
  void next.then(() => undefined, () => undefined).finally(() => { if (rowOnlyChains.get(key) === next) rowOnlyChains.delete(key); });
  return next;
}
/** Resolves once every row-only action queued for the actor has finished. */
export function rowOnlyIdle(actorIdRaw: unknown): Promise<void> {
  const chain = rowOnlyChains.get(upper(actorIdRaw));
  return chain ? chain.then(() => undefined, () => undefined) : Promise.resolve();
}
/**
 * POST one row-only command (call it inside runRowOnly): the SAME body (same operation id — the commit is idempotent on it) retried on a
 * lost answer or a 5xx, a 4xx answered at once. status 0 = never answered.
 */
export async function postRowOnly(url: string, body: Row, deps: ActorRowLoaderDeps = defaultActorRowLoaderDeps()): Promise<RowOnlyAnswer> {
  const doFetch = deps.fetchImpl ?? ((u: string, init: RequestInit) => fetch(u, init));
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)));
  const target = /^https?:/i.test(url) ? url : (deps.baseUrl?.() ?? '/').replace(/\/?$/, '/') + url.replace(/^\//, '');
  let last: RowOnlyAnswer = { status: 0, body: null };
  for (let attempt = 0; attempt < (deps.maxAttempts ?? 3); attempt++) {
    if (attempt > 0) await sleep(150 * 2 ** (attempt - 1));
    let response: Response;
    try {
      response = await doFetch(target, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    } catch { last = { status: 0, body: null }; continue; }
    const parsed = asRow(await response.json().catch(() => null));
    // A 2xx whose body never arrived is UNCERTAIN (the save may have landed): retry the same operation, never read it as a refusal
    // (Codex N6 review P2). Exhausted → status 0 ("could not be confirmed").
    if (response.ok && !parsed) { last = { status: 0, body: null }; continue; }
    last = { status: response.status, body: parsed };
    if (response.status < 500) return last;
  }
  return last;
}
